import React, {
  useEffect,
  useState,
  ChangeEvent,
  FormEvent,
  FC,
} from "react";
import { Modal } from "react-bootstrap";
import closeIcon from "../../assets/close-icon.svg"; // adjust path if needed

interface BillingInfoModalProps {
  show: boolean;
  onClose: () => void;
  initialData?: {
    billing_address?: string;
    authorised_signatory?: string;
  };
  onSubmit: (data: { billing_address: string; authorised_signatory: string }) => void;
}

type BillingFormState = {
  billing_address: string;
  authorised_signatory: string;
};

type BillingErrors = {
  billing_address?: string;
  authorised_signatory?: string;
};

export const BillingInfoModal: FC<BillingInfoModalProps> = ({
  show,
  onClose,
  initialData,
  onSubmit,
}) => {
  const [form, setForm] = useState<BillingFormState>({
    billing_address: "",
    authorised_signatory: "",
  });

  const [errors, setErrors] = useState<BillingErrors>({});

  useEffect(() => {
    if (show) {
      setForm({
        billing_address: initialData?.billing_address ?? "",
        authorised_signatory: initialData?.authorised_signatory ?? "",
      });
      setErrors({});
    }
  }, [show, initialData]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const validate = (data: BillingFormState): BillingErrors => {
    const e: BillingErrors = {};
    const address = data.billing_address.trim();
    const signatory = data.authorised_signatory.trim();

    if (!address) {
      e.billing_address = "field is required";
    } else if (address.length < 15) {
      e.billing_address = "Address can't have less than 15 characters";
    } else if (!/[A-Za-z0-9.,/\-\s]+$/.test(address)) {
      e.billing_address =
        "Address can't contain special characters other than . , / -";
    }

    if (!signatory) {
      e.authorised_signatory = "field is required";
    } else if (signatory.length < 3) {
      e.authorised_signatory = "Signatory can't have less than 3 characters";
    } else if (!/^[A-Za-z\s]+$/.test(signatory)) {
      e.authorised_signatory =
        "Signatory name can't contain special characters";
    }

    return e;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validationErrors = validate(form);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    onSubmit({
      billing_address: form.billing_address.trim(),
      authorised_signatory: form.authorised_signatory.trim(),
    });
  };

  const isDisabled =
    form.billing_address.trim().length < 15 ||
    form.authorised_signatory.trim().length < 3;
  return (
<Modal show={show} className="login-modal" centered>
        <Modal.Header className="justify-content-center login-modal-header border-0 position-relative">
          <div
            className="cursor-pointer login-modal-close text-center"
            onClick={onClose}
          >
            <img width="25" height="25" src={closeIcon} alt="close" />
          </div>
          <Modal.Title className="weight-400 text-sec-color">
            <span style={{ fontSize: "22px" }} >Please provide Billing Information</span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="login-modal-body border-0">
          <form className="register-form mf-new-user" onSubmit={handleSubmit} noValidate>
            <div className="register-input-container mb-3">
              <label
                className="mf-input-label text-capitalize font-13 weight-400"
                htmlFor="billing_address"
              >
                Billing Address
              </label>
              <input
                type="text"
                name="billing_address"
                id="billing_address"
                className="w-100 text-primary-color font-18 weight-400 mf-input-field"
                maxLength={200}
                minLength={7}
                pattern="[A-Za-z0-9.,/-\s]+"
                required
                onChange={handleChange}
                value={form.billing_address}
              />
              {errors.billing_address && (
                <p className="mb-0 error-msg weight-500">
                  {errors.billing_address}
                </p>
              )}
            </div>

            <div className="register-input-container mb-3">
              <label
                className="mf-input-label text-capitalize font-13 weight-400"
                htmlFor="authorised_signatory"
              >
                Authorized Signatory
              </label>
              <input
                type="text"
                name="authorised_signatory"
                id="authorised_signatory"
                className="w-100 text-primary-color font-18 weight-400 mf-input-field"
                maxLength={100}
                minLength={7}
                pattern="[A-Za-z\s]+"
                required
                onChange={handleChange}
                value={form.authorised_signatory}
              />
              {errors.authorised_signatory && (
                <p className="mb-0 error-msg weight-500">
                  {errors.authorised_signatory}
                </p>
              )}
            </div>

            <button
              disabled={isDisabled}
              className="mf-submit-btn text-capitalize primary-bg-color text-white border-0 w-100 mt-2 weight-400"
              type="submit"
            >
              Save
            </button>
          </form>
        </Modal.Body>
      </Modal>
  );
} 